// An audio interruption (iOS: a phone call, Siri, an alarm) as the phone page shows it. While
// iOS holds the audio the page says "Paused for a call"; when it lets go, the mic is fetched
// again in place if iOS allows it without a tap, otherwise the page asks for a tap. It never
// stays on hold: once the page is visible and focused again, 3 s of hold turns into "Tap to
// resume" (iOS sometimes never reports the end of a call it dismissed).
//
// Only held AUDIO is a call: the speaker's output, or the mic's audio context, "interrupted". A
// muted mic track alone is not (another app holds the mic, or a brief interruption left it
// muted while the speaker is fine): it never pauses the reply, and the page handles it as the
// mic's own state (./mic-reconnect.ts). Counting it here paused a reply that could play, and
// re-paused on the next tick after every tap while the track stayed muted.

export type HoldState = "none" | "call" | "tap";

export interface HoldInput {
  /** The phone's own session is running with its mic open. */
  active: boolean;
  /** iOS holds the audio (see audioHeld): never a muted mic track alone. */
  interrupted: boolean;
  /** The page is visible and focused. */
  attentive: boolean;
  /** The page is visible (focus aside: a fallback in case iOS never reports focus). */
  visible?: boolean;
}

/** What the page should do now. */
export type HoldAction =
  | { kind: "pause" } // say "Paused for a call"
  | { kind: "resume" } // the interruption ended: try to get the mic back without a tap
  | { kind: "tap"; why: "timeout" | "session_ended" | "resume_failed" }; // ask for a tap

/**
 * iOS holds the page's audio (a call): the speaker's output is "interrupted", or, while the mic
 * is on, its audio context is. A muted mic track is deliberately not an input: it is the mic's
 * own state, not a call.
 */
export function audioHeld(s: {
  speakerInterrupted: boolean;
  micOn: boolean;
  micContextInterrupted: boolean;
  /** The page's one audio context (mic and speaker share it) is "suspended". */
  micContextSuspended?: boolean;
  /** The mic's track is muted. */
  trackMuted?: boolean;
}): boolean {
  // iOS does not always say "interrupted" for a call: it can mute the mic's track and suspend the
  // shared context instead (a call through the car, 2026-10-15). Both at once, with the mic on,
  // is a call too: the context is the speaker's as well, so the reply can't be heard anyway. A
  // muted track alone (the speaker fine) still isn't.
  return s.speakerInterrupted || (s.micOn && s.micContextInterrupted) || (s.micOn && !!s.trackMuted && !!s.micContextSuspended);
}

/** The muted-and-suspended form of a call must hold this long (two 1 s ticks) to count. */
export const HELD_PERSIST_MS = 1000;

/**
 * Held audio as the page acts on it. An "interrupted" context is a call at once. The weaker form
 * (a muted track and a suspended context: audioHeld's third case) must last HELD_PERSIST_MS, and
 * never counts while the audio is busy: a Bluetooth route flip (car to AirPods) or a rebuild
 * (whose fresh context starts suspended) shows the same two for a moment, and paused the reply.
 */
export class HeldFilter {
  private since: number | null = null;
  /** The last answer (for a caller that must not advance the filter, e.g. on hide). */
  last = false;

  held(
    now: number,
    s: { speakerInterrupted: boolean; micOn: boolean; micContextInterrupted: boolean; micContextSuspended: boolean; trackMuted: boolean; busy: boolean },
  ): boolean {
    if (s.speakerInterrupted || (s.micOn && s.micContextInterrupted)) {
      this.since = null;
      return (this.last = true);
    }
    if (!s.busy && audioHeld(s)) {
      this.since ??= now;
      return (this.last = now - this.since >= HELD_PERSIST_MS);
    }
    this.since = null;
    return (this.last = false);
  }
}

/** How long the hold may last while the page is visible and focused. */
export const HOLD_GIVE_UP_MS = 3000;
/** How long the hold may last while the page is visible, focused or not. */
export const HOLD_VISIBLE_MAX_MS = 10_000;

export class HoldWatcher {
  state: HoldState = "none";
  private since = 0;
  /** When the page became attentive / visible (null: it isn't). */
  private attentiveSince: number | null = null;
  private visibleSince: number | null = null;
  private resuming = false;

  /** Call every tick (and on visibility, focus, pageshow); returns what to do, if anything. */
  tick(now: number, input: HoldInput): HoldAction | null {
    this.attentiveSince = input.attentive ? (this.attentiveSince ?? now) : null;
    this.visibleSince = input.visible || input.attentive ? (this.visibleSince ?? now) : null;
    if (this.state === "tap" && input.active && !input.interrupted) this.state = "none"; // the mic is back
    if (this.state === "tap" || this.resuming) return null;
    if (this.state === "none") {
      if (!input.active || !input.interrupted) return null;
      this.state = "call";
      this.since = now;
      return { kind: "pause" };
    }
    // On hold.
    if (!input.active) return this.toTap("session_ended");
    if (!input.interrupted) {
      this.resuming = true;
      return { kind: "resume" };
    }
    if (this.attentiveSince !== null && now - Math.max(this.since, this.attentiveSince) >= HOLD_GIVE_UP_MS) return this.toTap("timeout");
    if (this.visibleSince !== null && now - Math.max(this.since, this.visibleSince) >= HOLD_VISIBLE_MAX_MS) return this.toTap("timeout");
    return null;
  }

  /** The result of a "resume": the mic is back (on hold no more) or it needs a tap. */
  resumed(ok: boolean): HoldAction | null {
    this.resuming = false;
    if (this.state !== "call") return null;
    if (ok) {
      this.state = "none";
      return null;
    }
    return this.toTap("resume_failed");
  }

  /** The user tapped (or a new session started): whatever the hold was, it is over. */
  reset(): void {
    this.state = "none";
    this.resuming = false;
  }

  private toTap(why: "timeout" | "session_ended" | "resume_failed"): HoldAction {
    this.state = "tap";
    return { kind: "tap", why };
  }
}

// --- the hold, as the PC's log sees it, and the way back from the background ---------------------

/** Why the conversation is on hold: a call, a tap on the orb, the page in the background, or a
 * mic that iOS will only give back in a tap. */
export type HoldReason = "call" | "user" | "background" | "mic";
/** How a hold ended: carried on by itself, by a tap, or the session ended under it. */
export type HoldExit = "auto" | "tap" | "ended";

/**
 * One hold episode at a time, logged to the PC (`client.log`: `hold_entered`, `hold_exited`) with
 * its reason and how it ended, so a device test can be checked from the PC's log.
 */
export class HoldLog {
  private open: { reason: HoldReason; since: number } | null = null;

  constructor(
    private readonly report: (event: string, data: Record<string, unknown>) => void,
    private readonly now: () => number = () => Date.now(),
  ) {}

  /** The open hold's reason, or null. */
  get reason(): HoldReason | null {
    return this.open?.reason ?? null;
  }

  /** A hold starts (or, already on hold, its reason changes: logged as a new one). */
  enter(reason: HoldReason, extra: Record<string, unknown> = {}): void {
    if (this.open?.reason === reason) return;
    const previous = this.open?.reason ?? null;
    this.open = { reason, since: this.now() };
    this.report("hold_entered", { reason, previous, ...extra });
  }

  /** The hold ends; nothing is logged when there is none. */
  exit(how: HoldExit, extra: Record<string, unknown> = {}): void {
    const o = this.open;
    if (!o) return;
    this.open = null;
    this.report("hold_exited", { reason: o.reason, resume: how, heldMs: Math.round(this.now() - o.since), ...extra });
  }
}

/** A trip to the background this short comes back without a tap, where iOS allows it. */
export const AUTO_RESUME_MAX_MS = 60_000;

export interface ReturnInput {
  /** How long the page was hidden. */
  hiddenMs: number;
  /** The phone's voice was on when the page went (its mic open, or a reply of its being spoken). */
  voiceWasOn: boolean;
  /** The user had paused the reply before the page went: that pause is theirs to end. */
  userPaused: boolean;
  /** A call held the audio, when the page went or since. */
  call: boolean;
  /** The mic can be fetched again with no prompt (MicCapture.canStartQuietly: the permission is
   * "granted" and not a home-screen web app). Never a prompt without a gesture (PR #26). */
  quiet: boolean;
}

export type ReturnAction =
  | { kind: "none" } // voice was off: nothing to carry on
  | { kind: "auto" } // fetch the mic again and carry on, with no tap
  | { kind: "tap"; why: "user_paused" | "call" | "too_long" | "needs_gesture" }; // one tap does it all

/** What the page does when it comes back from the background. */
export function returnAction(s: ReturnInput): ReturnAction {
  if (!s.voiceWasOn) return { kind: "none" };
  if (s.userPaused) return { kind: "tap", why: "user_paused" };
  if (s.call) return { kind: "tap", why: "call" };
  if (s.hiddenMs >= AUTO_RESUME_MAX_MS) return { kind: "tap", why: "too_long" };
  if (!s.quiet) return { kind: "tap", why: "needs_gesture" };
  return { kind: "auto" };
}
